/**
 * Embedded Dashboard - Compact UI for Bitrix24 iframe
 *
 * Updated Architecture (AI Council recommendation):
 * - B24Frame SDK managed in layout (/embedded/__root.tsx)
 * - Receives B24Frame via route context
 * - No SDK initialization here (prevents destruction on navigation)
 *
 * Features:
 * - Dashboard: Real-time metrics from API with role-specific components
 * - Billing: Budget control, cost breakdown by provider
 * - Settings: Delivery configuration for Bitrix24 (CRM fields / Smart Processes)
 *
 * Layout:
 * - Tabs: Dashboard, Billing, Settings
 * - Role-specific dashboard components (AgentDashboard, SupervisorDashboard, etc.)
 * - All links open in new tab (_blank) to prevent iframe navigation issues
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { VOICE_OF_CUSTOMER_FLAG_KEY } from '@aica/core';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { objectionQuoteFor } from '@/components/dashboards/shared/highlights/useOpenRecord';
import { useTranslation } from 'react-i18next';

// Modular components
import { TabNavigation } from './components/TabNavigation';
import { DashboardTab } from './components/DashboardTab';
import { SettingsTab } from './components/SettingsTab';
import { RecordsTab } from './components/RecordsTab';
import { ObjectionsView } from '@/components/analytics/objections/ObjectionsView';
import { VoiceOfCustomerView } from '@/components/analytics/voice/VoiceOfCustomerView';
import {
  parseVoiceSearch,
  type VoiceSearch,
} from '@/components/analytics/voice/voice-search';
import { useOpenRecord } from '@/components/dashboards/shared/highlights/useOpenRecord';
import { useEmbeddedScreenSubject } from '@/contexts/ScreenSubjectContext';
import { dashboardFor } from '@/lib/route-permissions';
import {
  unifiedViewOf,
  useUnifiedDashboardEnabled,
} from '@/components/dashboards/unified/unified-flag';
import { useUnifiedSections } from '@/components/dashboards/unified/unified-gate';
import { activeSection } from '@/components/dashboards/unified/layout';
import {
  parseUnifiedDashboardSearch,
  type UnifiedDashboardSearch,
  type UnifiedSection,
} from '@/components/dashboards/unified/unified-search';
import { Alert, AlertClose, AlertDescription, AlertIcon } from '@/components/ui/alert';

// Types
import type { Tab } from './types';
import { embeddedTabAllowed, embeddedVoiceAccess, resolveEmbeddedTab } from './utils';

// Hooks
import { useEmbeddedLinksHandler } from '@/hooks/useEmbeddedLinksHandler';
import { useFitFrameOnResize } from '@/hooks/useFitFrameOnResize';
import {
  EMBEDDED_MIN_FRAME_HEIGHT_PX,
  fitContentHeightFrame,
} from '@/lib/embedded-content-sized';
import { useB24Frame } from '@/contexts/B24FrameContext';
import { useEmbeddedDemoData } from '@/hooks/useEmbeddedDemoData';
import { logDebug } from '@/lib/logger';

// Narrative
import { EmbeddedNarrative } from './components/EmbeddedNarrative';

// Тумблер анализа (embedded-двойник: cookie-сессия вместо Clerk)
import { ProcessingToggleBanner } from '@/features/processing/ProcessingToggleBanner';
import { BacklogCard } from '@/features/processing/BacklogCard';
import { useEmbeddedProcessingState } from '@/features/processing/useEmbeddedProcessingState';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { embeddedHostOffersDeliverySettings } from '@/lib/embedded-host';

// Поп-ап «что нового» (#946)
import { EmbeddedWhatsNew } from '@/features/whats-new';

// ============================================================================
// Component
// ============================================================================

/** The name a tab goes by, for the notice that it is not open to the subject. */
const TAB_LABEL_KEY: Readonly<Record<Tab, string>> = {
  dashboard: 'embedded.overview',
  records: 'embedded.records',
  settings: 'embedded.settings',
  objections: 'objectionsPage.title',
  voice: 'voicePage.title',
};

/** The two stream screens: their address carries their own window and rules. */
const isStreamTab = (tab: Tab | undefined): boolean =>
  tab === 'objections' || tab === 'voice';

function EmbeddedDashboard() {
  const { t } = useTranslation('dashboard');
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const locationState = useLocation().state;

  // Get B24Frame and user data from React Context (shared by layout)
  const { b24Frame } = useB24Frame();
  const { hasDemoData } = useEmbeddedDemoData();
  const processing = useEmbeddedProcessingState();
  // Embedded has no Clerk/RoleContext; the org comes from the cookie session.
  const {
    orgId,
    voiceOfCustomerEnabled,
    voiceOfCustomerUnchecked,
    refreshSession,
    crmVendor,
  } = useEmbeddedOrg();
  const { gate } = useFeatureFlagsSafe();
  const openRecord = useOpenRecord();
  const onOpenVoiceCall = useCallback(
    (recordId: string) => openRecord(recordId),
    [openRecord]
  );
  // Каждая вкладка — то же решение, что у шлюза портала (`decideScreen` там,
  // где он решает): роль решает только «Голос клиента» (оператору не открыт,
  // #848), у держателя гранта — его грант, когда `/access/me` ответил (#1645;
  // до ответа — роль, а вкладку, которую грант мог бы открыть, ждём). Тариф и
  // флаг «Голоса клиента» — `features` сессии. Проверяет всё это сервер.
  const subject = useEmbeddedScreenSubject();
  // «Settings» configures delivery into Bitrix24 fields and smart processes:
  // a host without them (Uspacy) has no such tab, by link or by address.
  const hostHasTab = (tab: Tab) =>
    tab !== 'settings' || embeddedHostOffersDeliverySettings(crmVendor);
  const isTabAllowed = (tab: Tab) => hostHasTab(tab) && embeddedTabAllowed(tab, subject);
  const resolution = resolveEmbeddedTab(
    search.tab && !hostHasTab(search.tab) ? undefined : search.tab,
    subject
  );
  const activeTab: Tab = resolution.tab;
  // The panel on screen: none while the answer that decides the tab is awaited.
  const shownTab: Tab | null = resolution.status === 'show' ? resolution.tab : null;
  const refusedByAccess =
    resolution.status === 'show' ? resolution.refusedByAccess : null;
  // The tab a link asked for and the grant refused — said, not swallowed: every
  // «open the call» (Voice of Customer, objections, highlights, an alert, an
  // assistant citation, the release pop-up) leads to `?tab=records`, and a
  // grant without `records.read` used to land on the overview with no word
  // (review #1678).
  const [refusedNotice, setRefusedNotice] = useState<Tab | null>(null);

  // The address's search parameters, patched in place — the stream views
  // (objections, Voice of Customer) and the unified shell all write through it.
  const onSearchPatch = useCallback(
    (patch: Partial<VoiceSearch> & Partial<UnifiedDashboardSearch>) =>
      void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true }),
    [navigate]
  );

  // The demo filter the unified shell was last seen with. `demo` is one
  // parameter for two meanings: the shell's filter, and a stream screen's rule
  // (`useStreamDemoFilter` reads it as an override of the organization's
  // policy). The strip carries neither across: a stream opened from it counts by
  // the policy, and the way back brings the shell's own choice back (#2561).
  const shellDemo = useRef<UnifiedDashboardSearch['demo']>(undefined);
  useEffect(() => {
    if (!isStreamTab(search.tab)) shellDemo.current = search.demo;
  }, [search.tab, search.demo]);

  const setActiveTab = useCallback(
    (tab: Tab, options?: { replace?: boolean; section?: UnifiedSection }) =>
      void navigate({
        replace: options?.replace,
        search: (previous) => {
          const toStream = isStreamTab(tab);
          const fromStream = isStreamTab(previous.tab);
          return {
            ...previous,
            tab,
            ...(options?.section ? { section: options.section } : {}),
            ...(tab === 'records'
              ? {}
              : {
                  recordId: undefined,
                  t: undefined,
                  clip: undefined,
                  alertId: undefined,
                }),
            ...(toStream
              ? // The shell's filter must not become the stream's override.
                fromStream
                ? {}
                : { demo: undefined }
              : {
                  period: undefined,
                  anchor: undefined,
                  // The calendar day of a card link's window: a stream screen's
                  // label, meaningless on the other tabs.
                  day: undefined,
                  // A stream screen's own demo override leaves with it, and the
                  // shell's filter it replaced comes back.
                  ...(fromStream ? { demo: shellDemo.current } : {}),
                  projectIds: undefined,
                  category: undefined,
                  view: undefined,
                  code: undefined,
                }),
            ...(tab === 'voice'
              ? {}
              : {
                  audience: undefined,
                  row: undefined,
                  voiceSection: undefined,
                  voiceCut: undefined,
                  voiceTheme: undefined,
                  from: undefined,
                  to: undefined,
                }),
          };
        },
      }),
    [navigate]
  );

  // A tab the grant refuses leaves the address too: a refused `?tab=` must not
  // stay there to be reloaded or shared (review #1678).
  useEffect(() => {
    if (!refusedByAccess) return;
    setRefusedNotice(refusedByAccess);
    setActiveTab('dashboard', { replace: true });
  }, [refusedByAccess, setActiveTab]);
  // The notice belongs to the overview it explains: any other tab ends it.
  useEffect(() => {
    if (shownTab !== 'dashboard') setRefusedNotice(null);
  }, [shownTab]);

  const onTabChange = (tab: Tab) => {
    setRefusedNotice(null);
    setActiveTab(tab);
  };

  // The unified dashboard (#1337): with its preview on, its tabs take the
  // Overview's place in the strip — one row, «Голос клиента» not twice: the
  // six business tabs, or the agent's own «Моё» (#1338 PR C).
  const unifiedEnabled = useUnifiedDashboardEnabled();
  const unifiedView = unifiedViewOf(
    dashboardFor(subject.globalRole, subject.access),
    unifiedEnabled
  );
  const { sections: unifiedSections, pending: unifiedPending } = useUnifiedSections(
    unifiedView ?? 'business'
  );
  const unifiedShown = unifiedView !== null;
  // The section on screen: each is a different height, and the iframe is as
  // tall as the host was told (by content, at the portal pane's width).
  const shownSection = unifiedShown
    ? activeSection(search.section, unifiedSections)
    : null;

  // Handle all links to open in new tab (prevent iframe navigation)
  useEmbeddedLinksHandler();

  // Adjust iframe size after content changes: a different tab or section, and
  // — through the observer — the content changing height under the same one
  // (cards finishing their loads, the custom period's row appearing).
  const rootRef = useRef<HTMLDivElement>(null);
  const fitFrame = useMemo(
    () =>
      b24Frame
        ? () => {
            fitContentHeightFrame(b24Frame.parent).catch((error: unknown) => {
              logDebug('embedded_dashboard.fit_window_failed', {
                error: error instanceof Error ? error.message : String(error),
              });
            });
          }
        : null,
    [b24Frame]
  );
  useEffect(() => {
    fitFrame?.();
  }, [activeTab, shownSection, fitFrame, hasDemoData, refusedNotice]);
  useFitFrameOnResize(rootRef, fitFrame);

  return (
    <div
      ref={rootRef}
      className="bg-bg"
      style={{ minHeight: EMBEDDED_MIN_FRAME_HEIGHT_PX }}
      data-testid="embedded-dashboard-root"
    >
      {/* Tabs Navigation */}
      <TabNavigation
        activeTab={activeTab}
        onTabChange={onTabChange}
        isTabAllowed={isTabAllowed}
        dashboardSections={
          unifiedShown
            ? {
                sections: unifiedSections,
                pending: unifiedPending,
                active: shownSection,
                onSelect: (section) => {
                  setRefusedNotice(null);
                  // From one section to another the entry is replaced, as on
                  // the portal's `/dashboard`: a tab of the shell is not a page
                  // to go back to. From another tab (Records, the full
                  // report) it is a way in, and stays one.
                  setActiveTab('dashboard', {
                    section,
                    replace: shownTab === 'dashboard',
                  });
                },
              }
            : undefined
        }
      />

      {refusedNotice && (
        <div className="px-4 pt-4">
          <Alert
            variant="info"
            dismissible
            onDismiss={() => setRefusedNotice(null)}
            data-testid="embedded-tab-refused"
          >
            <AlertIcon />
            <AlertDescription>
              {t('embedded.tabRefusedByAccess', {
                section: t(TAB_LABEL_KEY[refusedNotice]),
              })}
            </AlertDescription>
            <AlertClose />
          </Alert>
        </div>
      )}

      {/* Global onboarding nudge — collapses to nothing when hasDemoData is false */}
      <div className="px-4 pt-4">
        <EmbeddedNarrative />
      </div>

      {/* Тумблер анализа и накопленное. Здесь, а не только в портале: клиент
          вполне может не дойти до портала — весь первый час он живёт в CRM,
          и именно тут должен увидеть, что приём идёт, а анализ ещё не начат. */}
      <div className="px-4 pt-4">
        <ProcessingToggleBanner
          enabled={processing.enabled}
          // Весь накопленный объём, а не партия одного запуска: баннер говорит
          // «принято записей», а `records` обрезан серверным batchLimit.
          records={processing.summary?.totalRecords ?? 0}
          source={processing.summary?.source}
          isEnabling={processing.isEnabling}
          canStart={processing.canStart}
          onSetMode={processing.setMode}
          pausedReason={processing.summary?.pausedReason ?? null}
          pausedAt={processing.summary?.pausedAt ?? null}
          pausedFundsAvailable={processing.summary?.pausedFundsAvailable ?? null}
        />
        <BacklogCard
          canStart={processing.canStart}
          summary={processing.summary}
          isProcessing={processing.isProcessingBacklog}
          orgId={orgId}
          onProcess={processing.processBacklog}
          fetchRangeSummary={processing.fetchRangeSummary}
        />
      </div>

      {/* Content */}
      <div className="p-4">
        {shownTab === 'dashboard' && (
          // The shell's section is the panel's name (the shell, in this strip,
          // draws no panel of its own).
          <div
            role="tabpanel"
            tabIndex={0}
            aria-label={shownSection ? t(`unified.tabs.${shownSection}`) : undefined}
          >
            <DashboardTab
              unifiedView={unifiedView}
              unifiedSearch={search}
              onUnifiedSearchChange={onSearchPatch}
            />
          </div>
        )}
        {shownTab === 'settings' && (
          <div role="tabpanel" tabIndex={0}>
            <SettingsTab />
          </div>
        )}
        {/* Возражения (#846) — без кнопки в полосе вкладок: вход из карточки
            Quality, выход — обратно в обзор. */}
        {shownTab === 'objections' && (
          <div role="tabpanel" tabIndex={0} className="space-y-4">
            <button
              type="button"
              className="text-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => onTabChange('dashboard')}
            >
              ← {t('embedded.overview')}
            </button>
            <ObjectionsView search={search} onSearchChange={onSearchPatch} />
          </div>
        )}
        {shownTab === 'voice' && (
          <div role="tabpanel" tabIndex={0}>
            {/* Без ссылки на тарифы: оплаты во встроенной оболочке нет. */}
            <VoiceOfCustomerView
              search={search}
              onSearchChange={onSearchPatch}
              onOpenCall={onOpenVoiceCall}
              access={embeddedVoiceAccess(
                voiceOfCustomerEnabled,
                voiceOfCustomerUnchecked,
                gate(VOICE_OF_CUSTOMER_FLAG_KEY)
              )}
              onRetryAccess={() => void refreshSession()}
            />
          </div>
        )}
        {shownTab === 'records' && (
          <div role="tabpanel" tabIndex={0}>
            <RecordsTab
              initialRecordId={search.recordId}
              segmentStartSeconds={search.t}
              segmentDurationSeconds={search.clip}
              alertId={search.alertId}
              highlightQuote={
                search.recordId
                  ? objectionQuoteFor(locationState, search.recordId)
                  : undefined
              }
              onClearDeepLink={() => {
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    recordId: undefined,
                    t: undefined,
                    clip: undefined,
                    alertId: undefined,
                  }),
                });
              }}
            />
          </div>
        )}
      </div>

      {/* Поп-ап «что нового» (#946). Живёт внутри нашего iframe, поэтому
          интерфейс портала не перекрывает; сессии без связанного пользователя
          он не показывается — отметку «видел» там писать некуда. */}
      <EmbeddedWhatsNew />
    </div>
  );
}

// ============================================================================
// Route Configuration
// ============================================================================

// Search params from Bitrix24
interface EmbeddedDashboardSearch extends VoiceSearch, UnifiedDashboardSearch {
  DOMAIN?: string;
  PROTOCOL?: string;
  LANG?: string;
  APP_SID?: string;
  tab?: Tab;
  recordId?: string;
  t?: number;
  clip?: number;
  alertId?: string;
}

export const Route = createFileRoute('/embedded/dashboard/')({
  component: EmbeddedDashboard,
  validateSearch: (search: Record<string, unknown>): EmbeddedDashboardSearch => ({
    DOMAIN: search.DOMAIN as string | undefined,
    PROTOCOL: search.PROTOCOL as string | undefined,
    LANG: search.LANG as string | undefined,
    APP_SID: search.APP_SID as string | undefined,
    tab:
      search.tab === 'dashboard' ||
      search.tab === 'settings' ||
      search.tab === 'records' ||
      search.tab === 'objections' ||
      search.tab === 'voice'
        ? search.tab
        : undefined,
    ...parseVoiceSearch(search),
    // The unified dashboard's tab and filters (#1337) — names of its own.
    ...parseUnifiedDashboardSearch(search),
    recordId:
      typeof search.recordId === 'string' && search.recordId.length <= 200
        ? search.recordId
        : undefined,
    t: (() => {
      if (search.t === '' || search.t === null || search.t === undefined) {
        return undefined;
      }
      const value = Number(search.t);
      return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
    })(),
    clip: (() => {
      if (search.clip === '' || search.clip === null || search.clip === undefined) {
        return undefined;
      }
      const value = Number(search.clip);
      return Number.isSafeInteger(value) && value >= 1 && value <= 120
        ? value
        : undefined;
    })(),
    alertId:
      typeof search.alertId === 'string' && search.alertId.length <= 200
        ? search.alertId
        : undefined,
  }),
});

export default EmbeddedDashboard;
